/**
 * Onboarding stage funnel — the span the acquisition funnel collapses into a
 * single signup → onboarding_complete hop.
 *
 * NOT AcquisitionFunnel. That component's props are {stage, count,
 * pct_of_signup, pct_of_prior} — there is nowhere to put
 * `partially_instrumented`, so reusing it would silently drop the one signal
 * this funnel exists to carry, and render a missing-instrumentation step as a
 * confident drop-off. A separate renderer is the cost of telling the truth.
 *
 * All decisions about what the reader should believe live in
 * ../onboardingFunnelView (pure, unit-tested). This file is layout.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { CEDAR, SEMANTIC } from '../theme';
import { formatInt, formatPercent } from '../format';
import { barRows, instrumentationNotice } from '../onboardingFunnelView';
import type { OnboardingFunnelStep } from '../types';

export interface OnboardingStageFunnelProps {
  steps: OnboardingFunnelStep[];
}

export const OnboardingStageFunnel: React.FC<OnboardingStageFunnelProps> = ({ steps }) => {
  const rows = barRows(steps);
  const notice = instrumentationNotice(steps);

  return (
    <div className="space-y-3">
      {notice && (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p>{notice}</p>
        </div>
      )}

      <ol className="space-y-2">
        {rows.map((row) => (
          <li key={row.key} className="grid grid-cols-[11rem_1fr_auto] items-center gap-3">
            <span className="truncate text-sm text-gray-700" title={row.label}>
              {row.label}
              {row.state !== 'measured' && (
                <span className="ml-1 text-amber-700" title={
                  row.availableFrom
                    ? `Only collected from ${row.availableFrom} — earlier signups are not measured`
                    : 'Collection date unknown'
                }>*</span>
              )}
            </span>

            <div className="h-6 w-full rounded bg-gray-100">
              <div
                className="h-6 rounded"
                style={{
                  width: `${row.widthPct}%`,
                  // Hatched/amber for a step whose number is not comparable, so
                  // it cannot be read alongside the solid bars as an equal.
                  // Amber, not a lighter green: a not-comparable bar must not
                  // read as "same thing, just smaller".
                  backgroundColor: row.state === 'measured' ? CEDAR.prime : SEMANTIC.warning,
                }}
                role="img"
                aria-label={`${row.label}: ${row.count} users`}
              />
            </div>

            <span className="whitespace-nowrap text-right text-sm tabular-nums text-gray-900">
              {formatInt(row.count)}
              {row.pctOfCohort !== null && (
                <span className="ml-2 text-gray-500">{formatPercent(row.pctOfCohort)}</span>
              )}
              {row.showDropoff && row.dropoffFromPrior !== null && row.dropoffFromPrior > 0 && (
                <span className="ml-2 text-red-600" title="Lost since the previous step">
                  −{formatInt(row.dropoffFromPrior)}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};
